/**
 * HmProductReport — zestawienia towarowe: stany, obroty, progi, rotacja.
 *
 * Osiem raportów, jeden ekran — różnią się tym, KTÓRE KOLUMNY mają sens i które wiersze
 * przechodzą filtr. Kolumny dobieramy do raportu zamiast pokazywać wszystkie z kreskami:
 * kolumna „rotacja" w raporcie rezerwacji to szum, nie informacja.
 *
 * Dwa raporty mają ograniczenie po stronie Symfonii (stan wyłącznie bieżący, brak średniego
 * stanu do rotacji) — ekran mówi to wprost paskiem nad tabelą. Bez tego liczby wyglądałyby na
 * coś, czym nie są.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { HmProductReportRow } from '@saas/shared';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Download, Package, Printer, RefreshCw, SlidersHorizontal } from 'lucide-react';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import type { ColumnDefinition } from '@/components/configurable-table';
import { ConfigurableTable } from '@/components/configurable-table';
import { DatePicker } from '@/components/DatePicker';
import { Dropdown } from '@/components/Dropdown';
import { rangeFor } from '@/components/handel-magazyn/HmListShell';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import {
  defaultProductParams,
  fetchProductReport,
  isProductReportKind,
  parseProductSearchParams,
  productReportCaveatKey,
  type ProductReportParams,
  productReportTitleKey,
  productReportUsesDates,
  toProductSearchParams,
} from '@/lib/handel-magazyn/product-reports-api';
import { buildCsv, downloadCsv } from '@/lib/handel-magazyn/reports-api';
import { useActiveCompanyScope } from '@/lib/use-active-company';

const PRINT_PERMISSION = 'handel_magazyn.products.view';

/** Które kolumny ilościowe ma dany raport — reszta byłaby kolumną samych kresek. */
function columnsFor(kind: ProductReportParams['kind']): {
  stock: boolean;
  reservations: boolean;
  turnover: boolean;
  limits: boolean;
  rotation: boolean;
} {
  switch (kind) {
    case 'obroty-za-okres':
      return { stock: false, reservations: false, turnover: true, limits: false, rotation: false };
    case 'stany-na-dzien':
      return { stock: true, reservations: false, turnover: false, limits: false, rotation: false };
    case 'stany-i-rezerwacje':
      return { stock: true, reservations: true, turnover: false, limits: false, rotation: false };
    case 'stany-i-obroty':
      return { stock: true, reservations: false, turnover: true, limits: false, rotation: false };
    case 'ponizej-minimum':
    case 'powyzej-maksimum':
      return { stock: true, reservations: false, turnover: false, limits: true, rotation: false };
    case 'zalegajace':
      return { stock: true, reservations: true, turnover: true, limits: false, rotation: false };
    case 'rotacja':
      return { stock: true, reservations: false, turnover: true, limits: false, rotation: true };
  }
}

export function HmProductReport() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const companyId = useActiveCompanyScope().companyId;
  const { me } = useTenantAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { report } = useParams<{ report: string }>();

  const kind = isProductReportKind(report) ? report : 'stany-i-rezerwacje';
  const title = t(productReportTitleKey(kind));
  const caveatKey = productReportCaveatKey(kind);
  const shows = columnsFor(kind);
  const usesDates = productReportUsesDates(kind);

  const params = useMemo(() => parseProductSearchParams(searchParams, kind), [searchParams, kind]);
  const [printAll, setPrintAll] = useState(false);

  const canPrint =
    me?.user.isSuperUser === true || (me?.permissions.includes(PRINT_PERMISSION) ?? false);

  const query = useQuery({
    queryKey: ['handel-magazyn', 'product-report', companyId, searchParams.toString()],
    enabled: !!companyId,
    retry: false,
    queryFn: ({ signal }) => fetchProductReport(companyId!, params, signal),
  });

  const data = query.data;
  const rows = data?.rows ?? [];
  const totals = data?.totals;
  const warehouses = data?.warehouses ?? [];

  /**
   * Rozbicie na magazyny pokazujemy, gdy NIE wybrano konkretnego — wtedy pytanie brzmi „gdzie to
   * leży", a nie „ile tego jest". Przy wybranym magazynie kolumna per magazyn powtarzałaby
   * kolumnę stanu.
   */
  const splitByWarehouse = shows.stock && params.warehouse === null && warehouses.length > 1;

  /** Stan towaru w JEDNYM magazynie — `0`, gdy towar tam nie leży (a nie kreska: zero to fakt). */
  const stockIn = (row: HmProductReportRow, warehouse: string): string =>
    row.stocks.find((s) => s.warehouseName === warehouse)?.inStore ?? '0';

  /** Suma kolumny magazynu — liczona z wierszy, żeby zgadzała się z tym, co widać. */
  const warehouseTotal = (warehouse: string): string => {
    const milli = rows.reduce(
      (acc, row) => acc + Math.round(Number(stockIn(row, warehouse)) * 1000),
      0,
    );
    return String(milli / 1000);
  };

  function updateParams(patch: Partial<ProductReportParams>) {
    setSearchParams(toProductSearchParams({ ...params, ...patch }));
  }

  const columns = useMemo<ColumnDefinition<HmProductReportRow>[]>(() => {
    const out: ColumnDefinition<HmProductReportRow>[] = [
      {
        id: 'code',
        header: t('tenant.handelMagazyn.products.columns.code'),
        accessor: (row) => row.code,
        defaultWidth: 160,
        renderCell: (_v: unknown, row: HmProductReportRow) => (
          <Button
            variant="ghost"
            size="sm"
            className="h-auto justify-start px-0 py-0 font-mono text-xs font-normal text-text-primary underline-offset-2 hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/handel-magazyn/towary/${row.productId}`);
            }}
          >
            {row.code}
          </Button>
        ),
      },
      {
        id: 'name',
        header: t('tenant.handelMagazyn.products.columns.name'),
        accessor: (row) => row.name,
        defaultWidth: 280,
      },
      {
        id: 'unit',
        header: t('tenant.handelMagazyn.products.columns.unit'),
        accessor: (row) => row.unit ?? '—',
        defaultWidth: 70,
      },
    ];

    if (shows.stock) {
      out.push({
        id: 'inStore',
        header: t('tenant.handelMagazyn.productReports.columns.inStore'),
        accessor: (row) => row.inStore ?? '0',
        dataType: 'number',
        align: 'right',
        defaultWidth: 110,
      });
    }
    if (splitByWarehouse) {
      for (const warehouse of warehouses) {
        out.push({
          id: `wh-${warehouse}`,
          header: warehouse,
          accessor: (row) => stockIn(row, warehouse),
          dataType: 'number',
          align: 'right',
          defaultWidth: 110,
        });
      }
    }
    if (shows.reservations) {
      out.push(
        {
          id: 'toSale',
          header: t('tenant.handelMagazyn.productReports.columns.toSale'),
          accessor: (row) => row.toSale ?? '0',
          dataType: 'number',
          align: 'right',
          defaultWidth: 110,
        },
        {
          id: 'reserved',
          header: t('tenant.handelMagazyn.productReports.columns.reserved'),
          accessor: (row) => row.reserved ?? '0',
          dataType: 'number',
          align: 'right',
          defaultWidth: 110,
        },
      );
    }
    if (shows.limits) {
      out.push(
        {
          id: 'minState',
          header: t('tenant.handelMagazyn.products.minState'),
          accessor: (row) => row.minState ?? '—',
          align: 'right',
          defaultWidth: 100,
        },
        {
          id: 'maxState',
          header: t('tenant.handelMagazyn.products.maxState'),
          accessor: (row) => row.maxState ?? '—',
          align: 'right',
          defaultWidth: 100,
        },
      );
    }
    if (shows.turnover) {
      out.push(
        {
          id: 'income',
          header: t('tenant.handelMagazyn.productReports.columns.income'),
          accessor: (row) => row.income ?? '0',
          dataType: 'number',
          align: 'right',
          defaultWidth: 110,
        },
        {
          id: 'outcome',
          header: t('tenant.handelMagazyn.productReports.columns.outcome'),
          accessor: (row) => row.outcome ?? '0',
          dataType: 'number',
          align: 'right',
          defaultWidth: 110,
        },
      );
    }
    if (shows.rotation) {
      out.push({
        id: 'rotation',
        header: t('tenant.handelMagazyn.productReports.columns.rotation'),
        accessor: (row) => row.rotation ?? '—',
        align: 'right',
        defaultWidth: 100,
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, shows, navigate, splitByWarehouse, warehouses.join(',')]);

  function print() {
    setPrintAll(true);
    requestAnimationFrame(() => {
      window.print();
      setPrintAll(false);
    });
  }

  function exportCsv() {
    const csv = buildCsv([
      columns.map((c) => String(c.header)),
      ...rows.map((row) =>
        columns.map((c) => {
          // `accessor` bywa nazwą pola albo funkcją — CSV musi obsłużyć oba zapisy.
          const value = typeof c.accessor === 'function' ? c.accessor(row) : undefined;
          return value == null ? '' : String(value);
        }),
      ),
    ]);
    downloadCsv(`towary-${kind}.csv`, csv);
  }

  const errorInfo = useMemo(() => {
    if (!isAxiosError(query.error)) return null;
    const data = query.error.response?.data as { error?: string; message?: string } | undefined;
    return data?.message ?? data?.error ?? null;
  }, [query.error]);

  return (
    <div className="p-4">
      <div className="print-area">
        <div className="mb-3 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-text-primary">{title}</h1>
            <p className="text-sm text-text-secondary">
              {usesDates
                ? t('tenant.handelMagazyn.reports.rangeLabel', {
                    from: params.dateFrom,
                    to: params.dateTo,
                  })
                : t('tenant.handelMagazyn.productReports.currentState')}
              {params.warehouse ? ` · ${params.warehouse}` : ''}
              {data ? ` · ${data.integrationName}` : ''}
            </p>
          </div>
          <div className="no-print flex items-center gap-1">
            <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
              <RefreshCw className={`h-4 w-4 ${query.isFetching ? 'animate-spin' : ''}`} />
            </Button>
            {canPrint && (
              <>
                <Button variant="secondary" size="sm" onClick={exportCsv} disabled={!data}>
                  <Download className="mr-1.5 h-4 w-4" />
                  {t('tenant.handelMagazyn.toolbar.export')}
                </Button>
                <Button variant="secondary" size="sm" onClick={print} disabled={!data}>
                  <Printer className="mr-1.5 h-4 w-4" />
                  {t('tenant.handelMagazyn.toolbar.print')}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Ograniczenia kontraktu Symfonii — zawsze widoczne, także na wydruku. */}
        {caveatKey && (
          <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {t(caveatKey)}
          </div>
        )}

        <div className="no-print mb-3 flex flex-wrap items-center gap-2">
          {usesDates && (
            <>
              <SlidersHorizontal className="h-4 w-4 text-text-tertiary" />
              <DatePicker
                value={params.dateFrom}
                onChange={(value) => updateParams({ preset: 'custom', dateFrom: value })}
                aria-label={t('tenant.handelMagazyn.filters.dateFrom')}
              />
              <span className="text-text-tertiary">—</span>
              <DatePicker
                value={params.dateTo}
                onChange={(value) => updateParams({ preset: 'custom', dateTo: value })}
                aria-label={t('tenant.handelMagazyn.filters.dateTo')}
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  const range = rangeFor('thisMonth');
                  updateParams({ preset: 'thisMonth', dateFrom: range.from, dateTo: range.to });
                }}
              >
                {t('tenant.handelMagazyn.filters.preset.thisMonth')}
              </Button>
            </>
          )}
          {warehouses.length > 0 && (
            <div className="w-56">
              <Dropdown
                value={params.warehouse ?? ''}
                onChange={(value) => updateParams({ warehouse: value || null })}
                options={[
                  { value: '', label: t('tenant.handelMagazyn.productReports.allWarehouses') },
                  ...warehouses.map((w) => ({ value: w, label: w })),
                ]}
                data-testid="hm-product-report-warehouse"
              />
            </div>
          )}
          <Checkbox
            checked={params.includeEmpty}
            onChange={(e) => updateParams({ includeEmpty: e.target.checked })}
            label={t('tenant.handelMagazyn.productReports.includeEmpty')}
            data-testid="hm-product-report-include-empty"
          />
        </div>

        {errorInfo && (
          <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
            {errorInfo}
          </div>
        )}
        {data?.turnoverSkipped && (
          <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {t('tenant.handelMagazyn.productReports.turnoverSkipped')}
          </div>
        )}
        {(data?.turnoverFailed.length ?? 0) > 0 && (
          <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {t('tenant.handelMagazyn.productReports.turnoverFailed', {
              count: data!.turnoverFailed.length,
            })}
          </div>
        )}

        <div
          className="mb-2 flex flex-wrap items-center gap-x-6 gap-y-1 rounded-md border border-border-primary bg-bg-secondary px-3 py-2"
          data-testid="hm-product-report-totals"
        >
          <span className="text-sm font-semibold text-text-primary">
            {t('tenant.handelMagazyn.reports.grandTotal')}
          </span>
          <span className="text-xs text-text-secondary">
            {t('tenant.handelMagazyn.productReports.productCount', {
              count: totals?.products ?? 0,
            })}
          </span>
          {totals && shows.stock && (
            <span className="text-xs text-text-secondary">
              {t('tenant.handelMagazyn.productReports.columns.inStore')}{' '}
              <span className="font-mono text-text-primary">{totals.inStore}</span>
            </span>
          )}
          {totals && shows.reservations && (
            <span className="text-xs text-text-secondary">
              {t('tenant.handelMagazyn.productReports.columns.reserved')}{' '}
              <span className="font-mono text-text-primary">{totals.reserved}</span>
            </span>
          )}
          {totals && shows.turnover && (
            <>
              <span className="text-xs text-text-secondary">
                {t('tenant.handelMagazyn.productReports.columns.income')}{' '}
                <span className="font-mono text-text-primary">{totals.income}</span>
              </span>
              <span className="text-xs text-text-secondary">
                {t('tenant.handelMagazyn.productReports.columns.outcome')}{' '}
                <span className="font-mono text-text-primary">{totals.outcome}</span>
              </span>
            </>
          )}
        </div>

        <ConfigurableTable<HmProductReportRow>
          tableId={`hm_product_report_${kind}`}
          columns={columns}
          data={rows}
          isLoading={query.isLoading}
          getRowId={(row) => String(row.productId)}
          persistConfig={false}
          configurable={false}
          emptyIcon={Package}
          emptyTitle={t('tenant.handelMagazyn.productReports.empty')}
          testIdPrefix="hm-product-report"
          stickyHeader
          compact
          pageSize={printAll ? rows.length || 1 : 100}
          footerLabel={t('tenant.handelMagazyn.reports.grandTotal')}
          footerCells={(column) => {
            if (!totals) return null;
            if (column.id.startsWith('wh-')) return warehouseTotal(column.id.slice(3));
            switch (column.id) {
              case 'inStore':
                return totals.inStore;
              case 'toSale':
                return totals.toSale;
              case 'reserved':
                return totals.reserved;
              case 'income':
                return totals.income;
              case 'outcome':
                return totals.outcome;
              default:
                return null;
            }
          }}
        />
      </div>
    </div>
  );
}

export default HmProductReport;
